import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import CheckIn from "@/pages/CheckIn";
import Profile from "@/pages/Profile";
import Packages from "@/pages/Packages";
import Wallet from "@/pages/Wallet";
import Orders from "@/pages/Orders";
import Attendance from "@/pages/Attendance";
import Friends from "@/pages/Friends";
import Notifications from "@/pages/Notifications";
import Messages from "@/pages/Messages";
import Conversation from "@/pages/Conversation";
import GroupConversation from "@/pages/GroupConversation";
import CalendarView from "@/pages/CalendarView";
import Team from "@/pages/Team";
import RequestSong from "@/pages/RequestSong";
import DjBoard from "@/pages/DjBoard";
import PartyMode from "@/pages/PartyMode";
import DrinkScanner from "@/pages/DrinkScanner";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminDeleted from "@/pages/admin/AdminDeleted";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminClasses from "@/pages/admin/AdminClasses";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminReports from "@/pages/admin/AdminReports";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import AdminTerms from "@/pages/admin/AdminTerms";
import AdminScanner from "@/pages/admin/AdminScanner";
import AdminCodes from "@/pages/admin/AdminCodes";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminRedemptions from "@/pages/admin/AdminRedemptions";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminPurchases from "@/pages/admin/AdminPurchases";
import AdminPaymentMethods from "@/pages/admin/AdminPaymentMethods";
import AdminDesign from "@/pages/admin/AdminDesign";
import AdminSocialRoster from "@/pages/admin/AdminSocialRoster";

const Spinner = () => (
  <div className="min-h-[100dvh] flex items-center justify-center bg-[#0D0B14]">
    <div className="w-10 h-10 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin" />
  </div>
);

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  return user ? children : <Navigate to="/login" replace />;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  return user ? <Navigate to="/" replace /> : children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!["admin", "super_admin", "instructor", "staff"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RequireRoles({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/forgot" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/party" element={<RequireRoles roles={["dj", "admin", "super_admin"]}><PartyMode /></RequireRoles>} />
      <Route path="/drinks" element={<RequireRoles roles={["bartender", "staff", "instructor", "admin", "super_admin"]}><DrinkScanner /></RequireRoles>} />

      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/checkin" element={<CheckIn />} />
        <Route path="/calendar" element={<CalendarView />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/packages" element={<Packages />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/team" element={<Team />} />        <Route path="/messages" element={<Messages />} />
        <Route path="/request-song" element={<RequestSong />} />
        <Route path="/dj-board" element={<RequireRoles roles={["dj", "admin", "super_admin"]}><DjBoard /></RequireRoles>} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>

      <Route path="/messages/:friendId" element={<Protected><Conversation /></Protected>} />
      <Route path="/groups/:gid" element={<Protected><GroupConversation /></Protected>} />

      <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
        <Route index element={<AdminDashboard />} />
        <Route path="members" element={<AdminMembers />} />
        <Route path="deleted" element={<AdminDeleted />} />
        <Route path="catalog" element={<AdminCatalog />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="codes" element={<AdminCodes />} />
        <Route path="redemptions" element={<AdminRedemptions />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="purchases" element={<AdminPurchases />} />
        <Route path="payment-methods" element={<AdminPaymentMethods />} />
        <Route path="design" element={<AdminDesign />} />
        <Route path="classes" element={<AdminClasses />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="terms" element={<AdminTerms />} />
        <Route path="scanner" element={<AdminScanner />} />
        <Route path="drinks" element={<DrinkScanner embedded />} />
        <Route path="social-roster" element={<AdminSocialRoster />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="bottom-center" theme="dark" richColors closeButton offset={100} toastOptions={{ style: { cursor: "pointer" } }} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
